import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from "react";
import { BadgeCheckIcon, X, AlertCircle } from "lucide-react";
import moment from "moment-timezone";
import { SearchContext } from "src/common/search";
import { useRouter } from "next/router";
import { getLocalStorageUser } from "src/helpers/api/sharedFunction";
import { useSocket } from "src/components/SocketContext";

const IST_ZONE = "Asia/Kolkata";

const TIMER_MESSAGE = "New data loading in ";
const CHECKING_MESSAGE = "Checking for updates...";
const PROGRESS_MESSAGE =
  "Data update is in progress, please check after sometime";
const ERROR_MESSAGE =
  "Oops! Something went wrong, we are actively fixing it please check after some time";
const UPDATED_MESSAGE = "New data has been updated.";

/**
 * IST times that split the wait into popups. The server sends these with every
 * status payload (DATA_UPDATE_TIMER_START / _END / _CUTOFF), so they can be
 * retuned from the socket side alone — these are only the fallback used until
 * the first payload arrives.
 */
interface Schedule {
  start: string;
  end: string;
  cutoff: string;
}

const DEFAULT_SCHEDULE: Schedule = {
  start: "15:45",
  end: "17:00",
  cutoff: "17:30",
};

type DataUpdateStatus =
  | "holiday"
  | "waiting"
  | "updating"
  | "updated"
  | "error";

interface DataUpdateStatusPayload {
  date?: string;
  rundate?: string;
  status?: DataUpdateStatus;
  message?: string;
  schedule?: Partial<Schedule>;
}

/**
 * Five independent popups. Each owns its own dismissal key, so closing one
 * never hides the others — close the countdown and you still get
 * "Checking for updates..." at 5 PM, close that and you still get the green
 * "data updated" popup when the data actually lands.
 *
 *   TIMER    → between `start` and `end`, counting down to `end`
 *   CHECKING → between `end` and `cutoff`
 *   PROGRESS → after `cutoff`, still nothing
 *   SUCCESS  → status `updated`
 *   ERROR    → status `error`
 */
type Banner = "TIMER" | "CHECKING" | "PROGRESS" | "SUCCESS" | "ERROR";

/** Clock phase while the day's data has not landed yet. */
type Phase = "NONE" | "TIMER" | "CHECKING" | "PROGRESS";

/**
 * Dismissals persist FOR THE REST OF THE IST DAY. Clicking X stores today's
 * IST date under that popup's own key, and only that popup stays hidden across
 * refreshes until the date rolls over.
 */
const STORAGE_KEY_BY_BANNER: Record<Banner, string> = {
  TIMER: "global_notification_timer_closed_date",
  CHECKING: "global_notification_checking_closed_date",
  PROGRESS: "global_notification_progress_closed_date",
  SUCCESS: "global_notification_result_closed_date",
  ERROR: "global_notification_error_closed_date",
};

const BANNERS = Object.keys(STORAGE_KEY_BY_BANNER) as Banner[];

const KNOWN_STATUSES = new Set<DataUpdateStatus>([
  "holiday",
  "waiting",
  "updating",
  "updated",
  "error",
]);

/**
 * The `data_update_status` socket event is the only trigger for this widget.
 * The server owns the data side of every decision:
 *
 *   waiting  → before the monitor cutoff → keep showing the clock-based popup
 *   holiday  → rundate is in nse_holidays → show nothing
 *   updating → past cutoff, querylogstatus(queryid=1) has pending rows
 *   updated  → ALL querylogstatus rows for queryid=1 have status=1
 *   error    → the status check itself failed
 */
const istToday = (): string => moment().tz(IST_ZONE).format("YYYY-MM-DD");

const isHHmm = (value: unknown): value is string =>
  typeof value === "string" && /^\d{1,2}:\d{2}$/.test(value.trim());

/** Today at the given "HH:mm" IST. */
const atTime = (now: moment.Moment, hhmm: string): moment.Moment => {
  const [hour, minute] = hhmm.split(":").map(Number);
  return now.clone().startOf("day").hour(hour).minute(minute);
};

/** "HH:MM:SS" left until `target`, matching the original countdown format. */
const formatCountdown = (now: moment.Moment, target: moment.Moment): string => {
  const duration = moment.duration(Math.max(target.diff(now), 0));
  const pad = (n: number) => Math.floor(n).toString().padStart(2, "0");
  return `${pad(duration.hours())}:${pad(duration.minutes())}:${pad(duration.seconds())}`;
};

type DismissalMap = Record<Banner, string | null>;

/**
 * Dismissals live in a tiny module-level store rather than component state:
 * localStorage is an external system, so reading it through
 * useSyncExternalStore keeps the SSR/first client render identical (everything
 * null) and picks up writes from other tabs for free.
 */
const NO_DISMISSALS: DismissalMap = BANNERS.reduce(
  (acc, banner) => ({ ...acc, [banner]: null }),
  {} as DismissalMap,
);

let dismissalSnapshot: DismissalMap = NO_DISMISSALS;
const dismissalListeners = new Set<() => void>();
/** Fallback when localStorage is unavailable — keeps the tab's own closes. */
const sessionDismissals: Partial<DismissalMap> = {};

const readDismissals = (): DismissalMap =>
  BANNERS.reduce((acc, banner) => {
    let stored: string | null = null;
    try {
      stored = localStorage.getItem(STORAGE_KEY_BY_BANNER[banner]);
    } catch {
      /* private mode — fall back to this tab's own dismissals */
    }
    return { ...acc, [banner]: stored ?? sessionDismissals[banner] ?? null };
  }, {} as DismissalMap);

/** Re-read storage; notify subscribers only when something actually changed. */
const refreshDismissals = (): void => {
  const next = readDismissals();
  if (BANNERS.every((banner) => next[banner] === dismissalSnapshot[banner]))
    return;
  dismissalSnapshot = next;
  dismissalListeners.forEach((listener) => listener());
};

const subscribeDismissals = (onChange: () => void): (() => void) => {
  dismissalListeners.add(onChange);
  if (dismissalListeners.size === 1)
    window.addEventListener("storage", refreshDismissals);
  refreshDismissals();
  return () => {
    dismissalListeners.delete(onChange);
    if (dismissalListeners.size === 0)
      window.removeEventListener("storage", refreshDismissals);
  };
};

const getDismissalSnapshot = (): DismissalMap => dismissalSnapshot;
const getServerDismissalSnapshot = (): DismissalMap => NO_DISMISSALS;

export default function GlobalNotification() {
  const { setSelectedDate, selectedDate } = useContext(SearchContext);
  const router = useRouter();
  const { emit, on, off, isConnected } = useSocket();

  const [liveStatus, setLiveStatus] = useState<DataUpdateStatus | null>(null);
  /** Schedule the server last told us about. */
  const [schedule, setSchedule] = useState<Schedule>(DEFAULT_SCHEDULE);
  /** Clock phase plus the countdown text, refreshed every second. */
  const [clock, setClock] = useState<{ phase: Phase; timeLeft: string }>({
    phase: "NONE",
    timeLeft: "",
  });

  /** IST date each popup was last dismissed on (null = never dismissed). */
  const dismissedOn = useSyncExternalStore(
    subscribeDismissals,
    getDismissalSnapshot,
    getServerDismissalSnapshot,
  );

  /**
   * One-second tick, same as the original implementation — it drives the
   * countdown digits and rolls the popup on at `start`, over to "checking" at
   * `end`, and over to "in progress" at `cutoff`, all without a refresh.
   */
  useEffect(() => {
    const evaluate = () => {
      const now = moment().tz(IST_ZONE);
      const start = atTime(now, schedule.start);
      const end = atTime(now, schedule.end);
      const cutoff = atTime(now, schedule.cutoff);

      let next: { phase: Phase; timeLeft: string } = {
        phase: "NONE",
        timeLeft: "",
      };
      if (now.isSameOrAfter(cutoff)) next = { phase: "PROGRESS", timeLeft: "" };
      else if (now.isSameOrAfter(end))
        next = { phase: "CHECKING", timeLeft: "" };
      else if (now.isSameOrAfter(start))
        next = { phase: "TIMER", timeLeft: formatCountdown(now, end) };

      setClock((prev) =>
        prev.phase === next.phase && prev.timeLeft === next.timeLeft
          ? prev
          : next,
      );
    };

    evaluate();
    const interval = setInterval(evaluate, 1000);
    return () => clearInterval(interval);
  }, [schedule]);

  const user = useMemo(() => getLocalStorageUser() || {}, []);
  const isEligible = !!(user.is_subscribed || user.is_free);
  const isDashboard = router.pathname === "/dashboard";

  const handleStatus = useCallback(
    (payload: DataUpdateStatusPayload | null) => {
      // Take the times from the server so they stay tunable on the socket side.
      const incoming = payload?.schedule;
      if (incoming) {
        setSchedule((prev) => {
          const next: Schedule = {
            start: isHHmm(incoming.start) ? incoming.start.trim() : prev.start,
            end: isHHmm(incoming.end) ? incoming.end.trim() : prev.end,
            cutoff: isHHmm(incoming.cutoff)
              ? incoming.cutoff.trim()
              : prev.cutoff,
          };
          // Keep the same object when nothing changed — it is an effect
          // dependency, and the status event repeats every 30 s.
          return next.start === prev.start &&
            next.end === prev.end &&
            next.cutoff === prev.cutoff
            ? prev
            : next;
        });
      }

      const next = payload?.status;
      // Ignore anything unrecognised so a stray payload cannot wipe the popup.
      if (!next || !KNOWN_STATUSES.has(next)) return;
      console.log("[GlobalNotification] status →", next);
      setLiveStatus(next);
    },
    [],
  );

  useEffect(() => {
    if (!isConnected || !isEligible) return;

    on("data_update_status", handleStatus);
    emit("subscribe_data_update_status", { rundate: istToday() }, handleStatus);

    return () => {
      off("data_update_status", handleStatus);
      emit("unsubscribe_data_update_status");
    };
  }, [isConnected, isEligible, emit, on, off, handleStatus]);

  /** Which single popup status + clock ask for, before dismissal is applied. */
  const banner: Banner | null = useMemo(() => {
    switch (liveStatus) {
      case "updated":
        return "SUCCESS";
      case "error":
        return "ERROR";
      case "waiting":
      case "updating":
        // Data has not landed yet — the clock decides which popup that is.
        return clock.phase === "NONE" ? null : clock.phase;
      default:
        // holiday, or nothing received from the socket yet
        return null;
    }
  }, [liveStatus, clock.phase]);

  // Comparing against today's IST date (rather than a boolean flag) means a tab
  // left open past midnight IST sees the popup again on the new day.
  const isDismissed = banner !== null && dismissedOn[banner] === istToday();

  /** Hide one popup for the rest of the current IST day, across refreshes. */
  const dismiss = useCallback((target: Banner) => {
    const today = istToday();
    sessionDismissals[target] = today;
    try {
      localStorage.setItem(STORAGE_KEY_BY_BANNER[target], today);
    } catch {
      /* private mode — dismissal stays session-only */
    }
    refreshDismissals();
  }, []);

  const handleClose = useCallback(() => {
    if (banner) dismiss(banner);
  }, [banner, dismiss]);

  const handleRefresh = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const latestUser = getLocalStorageUser() || {};
      const lastSourceDate = latestUser.last_source_date
        ? moment(latestUser.last_source_date).tz(IST_ZONE).format("YYYY-MM-DD")
        : "";
      if (lastSourceDate) {
        setSelectedDate(moment.tz(lastSourceDate, IST_ZONE).toDate());
      }
      dismiss("SUCCESS");
    },
    [dismiss, setSelectedDate],
  );

  if (!isEligible || banner === null || isDismissed) return null;

  const renderSuccessMessage = () => {
    const latestUser = getLocalStorageUser() || {};
    const lastSourceDate = latestUser.last_source_date
      ? moment(latestUser.last_source_date).tz(IST_ZONE).format("YYYY-MM-DD")
      : "";
    const viewingDate = moment(selectedDate).tz(IST_ZONE).format("YYYY-MM-DD");

    // On the dashboard, offer to jump to the freshly fetched date when the
    // user is still looking at an older one.
    if (
      !latestUser?.is_trial &&
      isDashboard &&
      lastSourceDate &&
      viewingDate !== lastSourceDate
    ) {
      return (
        <>
          {UPDATED_MESSAGE} click{" "}
          <a
            href="#"
            onClick={handleRefresh}
            className="underline hover:text-green-700 font-bold"
          >
            here to refresh
          </a>
          .
        </>
      );
    }

    return UPDATED_MESSAGE;
  };

  /** The three waiting popups share the original amber pill styling. */
  const renderWaitingPill = (message: string, timeLeft?: string) => (
    <div className="inline-flex bg-amber-50 px-4 py-2.5 rounded-lg gap-3 items-center animate-blink shadow-lg border border-amber-100">
      <div className="w-2 h-2 bg-amber-600 rounded-full flex-shrink-0"></div>
      <span className="text-sm text-amber-900 whitespace-nowrap">
        {message}
      </span>
      {timeLeft && (
        <div className="bg-amber-600 text-white px-2.5 py-0.5 rounded font-mono font-bold text-sm flex-shrink-0">
          {timeLeft}
        </div>
      )}
      <button
        onClick={handleClose}
        className="text-amber-600 hover:text-amber-800 transition p-1 flex-shrink-0"
      >
        <X className="w-4 h-4 cursor-pointer" />
      </button>
    </div>
  );

  return (
    <>
      <style>{`
        @keyframes smoothBlink { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.03); } }
        .animate-blink { animation: smoothBlink 2s ease-in-out infinite; }
      `}</style>

      <div className="fixed bottom-4 right-4 z-[9999] ml-4">
        {banner === "TIMER" && renderWaitingPill(TIMER_MESSAGE, clock.timeLeft)}

        {banner === "CHECKING" && renderWaitingPill(CHECKING_MESSAGE)}

        {banner === "PROGRESS" && renderWaitingPill(PROGRESS_MESSAGE)}

        {banner === "SUCCESS" && (
          <div className="inline-flex bg-green-50 border-l-4 border-green-500 px-4 py-3 rounded shadow-lg gap-3 items-center">
            <BadgeCheckIcon className="w-5 h-5 text-green-600 flex-shrink-0" />
            <span className="text-green-900 font-medium text-sm">
              {renderSuccessMessage()}
            </span>
            <button
              onClick={handleClose}
              className="text-green-600 hover:text-green-800 transition p-1 flex-shrink-0"
            >
              <X className="w-4 h-4 cursor-pointer" />
            </button>
          </div>
        )}

        {banner === "ERROR" && (
          <div className="inline-flex bg-red-50 border-l-4 border-red-500 px-4 py-3 rounded-r gap-3 items-center">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <span className="text-red-900 font-medium text-sm whitespace-nowrap">
              {ERROR_MESSAGE}
            </span>
            <button
              onClick={handleClose}
              className="text-red-600 hover:text-red-800 transition p-1 flex-shrink-0"
            >
              <X className="w-4 h-4 cursor-pointer" />
            </button>
          </div>
        )}
      </div>
    </>
  );
}
